import { useEffect } from 'react';

import type { UseQueryResult } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

import { useUtilityContext } from '@change-corgi/core-react/utilityContext';
import type { QrCodeType } from '@change-corgi/middle-end-gql-schema';

import { useTrackOnce } from '#src/app/shared/hooks/tracking';
import { getDeviceAdjustedImageSize } from '#src/app/shared/utils/media';

import { getPetitionQRCode, type PetitionQRCodeResult } from './api';

const MAX_RETRIES = 3;
const QUERY_KEY = 'hooks-qrcode-graphql';

export type UsePetitionQRCodeOptions = {
	/**
	 * The petition slug or ID
	 */
	slugOrId: string;
	/**
	 * The QR code type (e.g., 'WEB_STARTER_DASHBOARD')
	 */
	type: QrCodeType;
	/**
	 * Size for the QR code (width and height, in pixels.  When not provided, the server
	 * configured size for the QR code type will be used.  When adjustForPixelRatio is true,
	 * size is required because the requested size must be calculated client-side.
	 */
	size?: number;
	/**
	 * Whether to adjust the size based on device pixel ratio for high-DPI displays.
	 * You generally only want to set this to true when the QR code is being displayed
	 * on screen.  When downloading or including in other assets, we use a fixed size.
	 * @default false
	 */
	adjustForPixelRatio?: boolean;
};

/**
 * Hook to fetch QR code data for a petition from the GraphQL API
 */
export function usePetitionQRCode(options: UsePetitionQRCodeOptions): UseQueryResult<PetitionQRCodeResult> {
	const { slugOrId, type, size, adjustForPixelRatio = false } = options;
	const utilityContext = useUtilityContext();
	const trackOnce = useTrackOnce();

	if (adjustForPixelRatio && !size) {
		throw new Error('size is required when adjustForPixelRatio is true');
	}

	const adjustedSize = size && adjustForPixelRatio ? getDeviceAdjustedImageSize(size) : size;

	const queryResult = useQuery({
		queryKey: [QUERY_KEY, { slugOrId, type, adjustedSize }],
		queryFn: async () => getPetitionQRCode({ slugOrId, type, size: adjustedSize, utilityContext }),
		retry: MAX_RETRIES,
	});

	useEffect(() => {
		// Track error event once when all retries are exhausted
		if (queryResult.isError && queryResult.failureCount >= MAX_RETRIES) {
			void trackOnce('petition_qr_code_failed', {
				petition_id: slugOrId,
				count: MAX_RETRIES,
				type: String(type),
			});
		}
	}, [queryResult.isError, queryResult.failureCount, trackOnce, slugOrId, type]);

	return queryResult;
}
